import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx"; // Import clsx for conditional classes
import styles from "./SparaTitle.module.scss";

const SparaTitle = ({ title, subtitle, rightComponent }) => {
    const [isSticky, setIsSticky] = useState(false);
    const sentinelRef = useRef(null); // Ref for the sentinel element
    const containerRef = useRef(null); // Ref for the sticky container

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // If sentinel is NOT intersecting (i.e., scrolled past the top)
                // AND the container itself exists, then it should be sticky.
                // We check containerRef.current to avoid potential issues during unmount.
                setIsSticky(!entry.isIntersecting && containerRef.current);
            },
            { threshold: [0] } // Trigger when sentinel is 0% visible
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) {
            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, []);

    return (
        <>
            {/* Sentinel element just above the sticky container */}
            <div
                ref={sentinelRef}
                style={{ height: "1px", position: "relative", top: "-1px" }}
            ></div>
            <div
                ref={containerRef} // Add ref to the container
                className={clsx(styles.container, { [styles.isSticky]: isSticky })} // Conditionally add isSticky class
            >
                <div>
                    <h1 className={styles.title}>{title}</h1>
                    {subtitle && (
                        <p className={styles.subtitle}>
                            {subtitle.split("\n").map((line, i, arr) => (
                                <React.Fragment key={i}>
                                    {line}
                                    {i < arr.length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                </div>
                {rightComponent && <div className={styles.rightContent}>{rightComponent}</div>}
            </div>
        </>
    );
};

SparaTitle.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    rightComponent: PropTypes.node,
};

export default SparaTitle;
